import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  BedDouble,
  Bath,
  Car,
  Waves,
  Snowflake,
  Phone,
  X,
  Maximize2,
  FileText,
  ChevronRight,
  Home,
  TreePine,
  Camera,
} from "lucide-react";

import { useImageProcessing } from "./hooks/useImageProcessing";
import GaleriaCompleta from "./hooks/GaleriaCompleta";
import FichaTecnica from "./hooks/FichaTecnica";
import api from "../api/axios";

export default function ModalPropiedad({
  propiedad,
  onClose,
  isModal = true,
  showCloseButton = true,
  fullScreen = false,
}) {
  const [visible, setVisible] = useState(false);
  const [showFicha, setShowFicha] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [currentGalleryImage, setCurrentGalleryImage] = useState(0);
  const [propiedadCompleta, setPropiedadCompleta] = useState(null);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(false);
  const resumenRef = useRef(null);

  const propiedadActiva = propiedadCompleta || propiedad;

  const { imagenesProcesadas, getImageUrl, getSafeImageUrl } =
    useImageProcessing(propiedadActiva);

  useEffect(() => {
    if (!propiedad?.id) {
      setPropiedadCompleta(null);
      return;
    }

    setCargandoDetalle(true);
    api
      .get(`/properties/${propiedad.id}`)
      .then((res) => {
        setPropiedadCompleta(res.data);
      })
      .catch((error) => {
        console.error("Error cargando detalles:", error);
        setPropiedadCompleta(propiedad);
      })
      .finally(() => {
        setCargandoDetalle(false);
      });
  }, [propiedad]);

  useEffect(() => {
    if (propiedadActiva) {
      setTimeout(() => setVisible(true), 10);
    }
  }, [propiedadActiva]);

  useEffect(() => {
    const elementoResumen = resumenRef.current;

    if (!elementoResumen) return;

    const evaluarLayout = () => {
      setIsCompactLayout(elementoResumen.offsetWidth < 420);
    };

    evaluarLayout();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", evaluarLayout);

      return () => window.removeEventListener("resize", evaluarLayout);
    }

    const observer = new ResizeObserver(evaluarLayout);
    observer.observe(elementoResumen);
    window.addEventListener("resize", evaluarLayout);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", evaluarLayout);
    };
  }, [propiedadActiva, isModal, showFicha, showGallery, cargandoDetalle]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (showGallery) {
        event.preventDefault();
        setShowGallery(false);
        return;
      }

      if (showFicha) {
        event.preventDefault();
        setShowFicha(false);
        return;
      }

      event.preventDefault();
      onClose?.();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, showFicha, showGallery]);

  if (!propiedad) return null;

  const c = propiedadActiva?.caracteristicas || {};

  const nextImage = () => {
    setCurrentGalleryImage((prev) =>
      prev === imagenesProcesadas.length - 1 ? 0 : prev + 1,
    );
  };

  const prevImage = () => {
    setCurrentGalleryImage((prev) =>
      prev === 0 ? imagenesProcesadas.length - 1 : prev - 1,
    );
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const imagesForPrint = imagenesProcesadas.map((img) =>
      getSafeImageUrl(img),
    );

    const secondaryImagesHTML =
      imagesForPrint.slice(1).length > 0
        ? `
        <div class="section" style="page-break-before: auto;">
          <div class="section-title">Vistas Adicionales</div>
          <div class="gallery-grid">
            ${imagesForPrint
              .slice(1)
              .map(
                (imgUrl, index) => `
              <div class="gallery-item">
                <img src="${imgUrl || "https://via.placeholder.com/400x300?text=Inmueble"}" alt="Vista ${index + 2}" 
                     onerror="this.src='https://via.placeholder.com/400x300?text=Imagen+No+Disponible'"/>
              </div>
            `,
              )
              .join("")}
          </div>
        </div>`
        : "";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="es">
      <head>
        <meta charset="UTF-8">
        <title>Ficha Profesional - ${propiedadActiva?.titulo || "Propiedad"}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');
          * { box-sizing: border-box; }
          body {
            font-family: 'Outfit', sans-serif;
            margin: 0; padding: 40px;
            color: #1e293b; line-height: 1.5;
            background-color: white;
          }
          .ficha-container { max-width: 900px; margin: 0 auto; }
          .brand-header {
            display: flex; justify-content: space-between; align-items: center;
            border-bottom: 4px solid #1e40af; padding-bottom: 15px; margin-bottom: 30px;
          }
          .logo-text { font-weight: 800; font-size: 22px; color: #1e40af; letter-spacing: -0.5px; }
          .web-link { font-size: 12px; color: #64748b; font-weight: 600; }
          .property-header { margin-bottom: 25px; }
          .property-id { font-size: 11px; font-weight: 800; color: #3b82f6; text-transform: uppercase; letter-spacing: 2px; }
          .title { font-size: 32px; font-weight: 800; margin: 5px 0; color: #0f172a; line-height: 1.1; }
          .location { font-size: 14px; color: #64748b; display: flex; align-items: center; gap: 5px; }
          .highlight-bar {
            background: #f8fafc; border-radius: 12px; padding: 20px;
            display:grid; grid-template-columns: 1.5fr 1fr 1fr 1fr; gap: 15px;
            margin: 25px 0; border: 1px solid #e2e8f0;
          }
          .h-item label { display: block; font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; margin-bottom: 4px; }
          .h-item span { font-size: 16px; font-weight: 600; color: #1e293b; }
          .h-item.price-box span { color: #1e40af; font-size: 22px; font-weight: 800; }
          .hero-image { width: 100%; height: 420px; object-fit: cover; border-radius: 16px; margin-bottom: 25px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
          .gallery-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .gallery-item img { width: 100%; height: 200px; object-fit: cover; border-radius: 10px; border: 1px solid #e2e8f0; }
          .section { margin-top: 35px; page-break-inside: avoid; }
          .section-title {
            font-size: 14px; font-weight: 800; text-transform: uppercase;
            letter-spacing: 1.5px; color: #1e40af; border-left: 4px solid #3b82f6;
            padding-left: 10px; margin-bottom: 15px;
          }
          .description { font-size: 14px; color: #475569; text-align: justify; columns: 2; column-gap: 30px; }
          .amenities-container {
            display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
            background: #f1f5f9; padding: 20px; border-radius: 12px;
          }
          .amenity-tag { font-size: 12px; font-weight: 500; display: flex; align-items: center; gap: 6px; }
          .footer {
            margin-top: 50px; padding-top: 20px; border-top: 1px solid #e2e8f0;
            display: flex; justify-content: space-between; align-items: flex-end;
          }
          .contact-info { font-size: 11px; color: #64748b; line-height: 1.8; }
          .qr-placeholder { width: 60px; height: 60px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #cbd5e1; border: 1px solid #e2e8f0; }
          @media print {
            body { padding: 0; }
            .hero-image { height: 350px; }
            .highlight-bar { -webkit-print-color-adjust: exact; background-color: #f8fafc !important; }
            .section-title { -webkit-print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="ficha-container">
          <header class="brand-header">
            <div class="logo-text">INMOBILIARIA <span style="color:#3b82f6">PREMIUM</span></div>
            <div class="web-link">www.inmobiliariapremium.com</div>
          </header>

          <main>
            <div class="property-header">
              <span class="property-id">Referencia: PRO-${(propiedadActiva?.id || propiedad.id).toString().padStart(4, "0")}</span>
              <h1 class="title">${propiedadActiva?.titulo || propiedad.titulo}</h1>
              <div class="location">📍 ${propiedadActiva?.ubicacion || propiedad.ubicacion}</div>
            </div>

            ${imagesForPrint.length > 0 ? `<img src="${imagesForPrint[0] || "https://via.placeholder.com/800x600?text=Imagen+Principal"}" class="hero-image" />` : ""}

            <div class="highlight-bar">
              <div class="h-item price-box">
                <label>Precio de Lista</label>
                <span>$${Number(propiedadActiva?.precio || propiedad.precio).toLocaleString()} MXN</span>
              </div>
              <div class="h-item">
                <label>Construcción</label>
                <span>${c.area || "--"} m²</span>
              </div>
              <div class="h-item">
                <label>Terreno</label>
                <span>${c.terreno || "--"} m²</span>
              </div>
              <div class="h-item">
                <label>Habitaciones</label>
                <span>${c.recamaras || 0} Rec. / ${c.banos || 0} Baños</span>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Descripción General</div>
              <div class="description">${propiedadActiva?.descripcion || propiedad.descripcion || "No hay descripción disponible para esta propiedad."}</div>
            </div>

            <div class="section">
              <div class="section-title">Equipamiento y Amenidades</div>
              <div class="amenities-container">
                ${[
                  { v: c.alberca, t: "Alberca Privada" },
                  { v: c.vigilancia, t: "Seguridad 24/7" },
                  { v: c.aire_acondicionado, t: "Aire Acondicionado" },
                  { v: c.cocina_equipada, t: "Cocina Integral" },
                  { v: c.jardin, t: "Área de Jardín" },
                  { v: c.pet_friendly, t: "Mascotas Permitidas" },
                  { v: c.terraza, t: "Terraza / Balcón" },
                  { v: c.amueblado, t: "Amueblado" },
                  { v: c.internet, t: "Internet de Alta Velocidad" },
                ]
                  .map((a) =>
                    a.v ? `<div class="amenity-tag">🔹 ${a.t}</div>` : "",
                  )
                  .join("")}
              </div>
            </div>

            ${secondaryImagesHTML}
          </main>

          <footer class="footer">
            <div class="contact-info">
              <strong>Inmobiliaria Premium S.A. de C.V.</strong><br>
              Mérida, Yucatán, México<br>
              📞 999 999 9999 | ✉️ contacto@inmobiliaria.com<br>
              <span style="color:#94a3b8">Documento informativo. Sujeto a cambios sin previo aviso.</span>
            </div>
            <div class="qr-placeholder">SCAN<br>INFO</div>
          </footer>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
    }, 700);
  };

  const handleDownloadPDF = () => {
    handlePrint();
  };

  const renderPortal = (content) => {
    if (typeof document === "undefined") return content;

    return createPortal(content, document.body);
  };

  if (!propiedadActiva) return null;

  if (showGallery) {
    return renderPortal(
      <GaleriaCompleta
        imagenesProcesadas={imagenesProcesadas}
        currentGalleryImage={currentGalleryImage}
        onClose={() => setShowGallery(false)}
        onNext={nextImage}
        onPrev={prevImage}
        getSafeImageUrl={getSafeImageUrl}
      />,
    );
  }

  if (showFicha) {
    return renderPortal(
      <FichaTecnica
        propiedad={propiedadActiva}
        imagenesProcesadas={imagenesProcesadas}
        onClose={() => setShowFicha(false)}
        getSafeImageUrl={getSafeImageUrl}
        getImageUrl={getImageUrl}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        handleDownloadPDF={handleDownloadPDF}
      />,
    );
  }

  if (cargandoDetalle) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 bg-slate-900/40 backdrop-blur-sm">
        <div className="bg-white rounded-[32px] shadow-2xl max-w-4xl w-full overflow-hidden relative p-12 flex flex-col items-center justify-center">
          <div className="w-12 h-12 border-2 border-slate-200 border-t-blue-600 animate-spin rounded-full mb-4"></div>
          <p className="text-slate-600 font-medium">
            Cargando detalles completos...
          </p>
          <p className="text-slate-400 text-sm mt-2">
            ID: PRO-{propiedad.id.toString().padStart(4, "0")}
          </p>
        </div>
      </div>
    );
  }

  const renderThumbnailTile = (img, i) => {
    const imageObj = img || {};
    const imageUrl = getSafeImageUrl(img);

    return (
      <div
        key={i}
        className="aspect-square rounded-xl overflow-hidden border border-white shadow-sm relative group"
      >
        <img
          src={imageUrl}
          alt={`thumb-${i}`}
          className="w-full h-full object-cover hover:opacity-80 transition-opacity"
          onError={(e) => {
            console.error("Error cargando miniatura:", img);
            e.target.src = "https://via.placeholder.com/200x200?text=Imagen";
          }}
        />

        {imageObj.tags && imageObj.tags.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex flex-wrap gap-1">
              {imageObj.tags.slice(0, 2).map((tag, tagIndex) => (
                <span
                  key={tagIndex}
                  className="text-[8px] text-white bg-white/30 backdrop-blur-sm px-1.5 py-0.5 rounded-full"
                >
                  {tag}
                </span>
              ))}
              {imageObj.tags.length > 2 && (
                <span className="text-[8px] text-white/80">
                  +{imageObj.tags.length - 2}
                </span>
              )}
            </div>
          </div>
        )}

        {imageObj.descripcion && (
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <p className="text-white text-[10px] p-2 text-center">
              {imageObj.descripcion}
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderGalleryTile = () => (
    <button
      onClick={() => setShowGallery(true)}
      className="aspect-square rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900 hover:border-blue-300 transition-all group flex flex-col items-center justify-center gap-2 shadow-sm"
    >
      <Maximize2
        size={14}
        className="group-hover:scale-110 transition-transform"
      />
      <span className="text-[9px] font-bold uppercase tracking-widest leading-tight text-center px-2">
        Explorar Galería
      </span>
      <span className="text-[9px] text-slate-400">
        ({imagenesProcesadas.length})
      </span>
    </button>
  );

  const renderVisualSection = () => (
    <div className="p-4 bg-slate-50">
      {isCompactLayout ? (
        <div className="flex gap-3 items-stretch">
          <div className="relative group overflow-hidden rounded-2xl aspect-square w-[38%] shrink-0">
            {imagenesProcesadas.length > 0 ? (
              <img
                src={getSafeImageUrl(imagenesProcesadas[0])}
                alt="Main"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  console.error(
                    "Error cargando imagen principal:",
                    imagenesProcesadas[0],
                  );
                  e.target.src =
                    "https://via.placeholder.com/800x600?text=Imagen+Principal";
                }}
              />
            ) : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                <Camera size={40} className="text-slate-300" />
              </div>
            )}
            <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest text-blue-600 border border-blue-100">
              {propiedadActiva.zona || "Norte"}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 flex-1 min-w-0">
            {imagenesProcesadas
              .slice(1, 4)
              .map((img, i) => renderThumbnailTile(img, i))}
            {renderGalleryTile()}
          </div>
        </div>
      ) : (
        <>
          <div className="relative group overflow-hidden rounded-2xl aspect-video md:aspect-square">
            {imagenesProcesadas.length > 0 ? (
              <img
                src={getSafeImageUrl(imagenesProcesadas[0])}
                alt="Main"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                onError={(e) => {
                  console.error(
                    "Error cargando imagen principal:",
                    imagenesProcesadas[0],
                  );
                  e.target.src =
                    "https://via.placeholder.com/800x600?text=Imagen+Principal";
                }}
              />
            ) : (
              <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                <Camera size={48} className="text-slate-300" />
              </div>
            )}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-blue-600 border border-blue-100">
              {propiedadActiva.zona || "Norte"}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-2 mt-3">
            {imagenesProcesadas
              .slice(1, 5)
              .map((img, i) => renderThumbnailTile(img, i))}

            {imagenesProcesadas.slice(1).length < 4 &&
              Array.from({
                length: 4 - imagenesProcesadas.slice(1).length,
              }).map((_, i) => (
                <div
                  key={`placeholder-${i}`}
                  className="aspect-square rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center"
                >
                  <Camera size={20} className="text-slate-300" />
                </div>
              ))}
          </div>

          {imagenesProcesadas.length > 0 && (
            <button
              onClick={() => setShowGallery(true)}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 border border-slate-200 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-500 hover:bg-white hover:text-slate-900 hover:border-blue-300 transition-all group"
            >
              Explorar Galería ({imagenesProcesadas.length}){" "}
              <Maximize2
                size={14}
                className="group-hover:scale-110 transition-transform"
              />
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderDetailSection = () => (
    <div
      className={`flex flex-col justify-between ${isCompactLayout ? "p-6" : "p-8 md:p-10"}`}
    >
      <div className="space-y-3">
        <div>
          <span
            className={`text-[10px] font-black uppercase tracking-[0.2em] mb-2 block ${
              propiedadActiva.caracteristicas?.tipo_operacion === "renta"
                ? "text-blue-600"
                : "text-green-600"
            }`}
          >
            {propiedadActiva.caracteristicas?.tipo_operacion || "Venta"}
          </span>
          <h2 className="text-3xl font-serif italic text-slate-900 leading-tight">
            {propiedadActiva.titulo}
          </h2>
          <p className="text-slate-400 text-xs mt-2 uppercase tracking-widest font-medium">
            ID: PRO-{propiedadActiva.id.toString().padStart(4, "0")}
          </p>
        </div>

        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-light text-slate-900">
            ${Number(propiedadActiva.precio).toLocaleString()}
          </span>
          <span className="text-xs text-slate-400 font-medium uppercase tracking-widest">
            MXN
          </span>
        </div>

        <p className="text-slate-500 text-sm leading-relaxed line-clamp-4 font-light italic border-l-2 border-slate-100 pl-4">
          "
          {propiedadActiva.descripcion ||
            "Una oportunidad única para elevar tu estilo de vida en una ubicación privilegiada."}
          "
        </p>

        <div className="grid grid-cols-3 gap-1 py-2 border-y border-slate-50">
          <div className="flex flex-col items-center gap-1">
            <BedDouble size={20} className="text-slate-300" />
            <span className="text-xs font-bold text-slate-700">
              {c.recamaras || 0}
            </span>
            <span className="text-[9px] uppercase text-slate-400 tracking-tighter">
              Habitaciones
            </span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Bath size={20} className="text-slate-300" />
            <span className="text-xs font-bold text-slate-700">
              {c.banos || 0}
            </span>
            <span className="text-[9px] uppercase text-slate-400 tracking-tighter">
              Baños
            </span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <Car size={20} className="text-slate-300" />
            <span className="text-xs font-bold text-slate-700">
              {c.estacionamiento || 0}
            </span>
            <span className="text-[9px] uppercase text-slate-400 tracking-tighter">
              Cochera
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 text-slate-400">
          {c.alberca && (
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
              <Waves size={14} /> Alberca
            </div>
          )}
          {c.aire_acondicionado && (
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
              <Snowflake size={14} /> A/C
            </div>
          )}
          {c.jardin && (
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
              <TreePine size={14} /> Jardín
            </div>
          )}
          {c.terraza && (
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest">
              <Home size={14} /> Terraza
            </div>
          )}
        </div>
      </div>

      <div
        className={`mt-4 flex items-center justify-between gap-4 ${isCompactLayout ? "flex-col items-stretch" : ""}`}
      >
        <div className="flex flex-col">
          <span className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1">
            Contacto Directo
          </span>
          <div className="flex items-center gap-2 text-slate-900 font-medium">
            <Phone size={14} className="text-blue-600" />
            <span className="text-sm">999 999 9999</span>
          </div>
        </div>
        <button
          onClick={() => setShowFicha(true)}
          className={`flex-1 bg-slate-900 text-white py-4 rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-blue-700 transition-all shadow-xl shadow-slate-900/10 flex items-center justify-center gap-2 group ${isCompactLayout ? "w-full max-w-none" : "max-w-[180px]"}`}
        >
          <FileText size={14} />
          Ver Ficha{" "}
          <ChevronRight
            size={14}
            className="group-hover:translate-x-1 transition-transform"
          />
        </button>
      </div>
    </div>
  );

  const resumen = (
    <div
      ref={resumenRef}
      className={`bg-white overflow-hidden relative transform transition-all duration-500 ease-out ${
        fullScreen
          ? "h-full w-full rounded-none shadow-none"
          : "rounded-[32px] shadow-2xl max-w-4xl w-full"
      } ${visible ? "scale-100 translate-y-0" : "scale-95 translate-y-12"}`}
      onClick={(e) => e.stopPropagation()}
    >
      {showCloseButton && (
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-10 p-2 bg-white/80 backdrop-blur-md hover:bg-white rounded-full shadow-lg border border-slate-100 transition-all text-slate-400 hover:text-slate-900"
        >
          <X size={20} />
        </button>
      )}

      <div
        className={`${isCompactLayout ? "flex flex-col" : "grid grid-cols-1 md:grid-cols-2"}`}
      >
        {renderVisualSection()}
        {renderDetailSection()}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div
        className={`fixed inset-0 z-[100] flex items-stretch justify-stretch bg-slate-950/35 backdrop-blur-sm transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        } ${fullScreen ? "p-0" : "p-4 md:p-6 items-center justify-center"}`}
        onClick={onClose}
      >
        {resumen}
      </div>
    );
  }

  return resumen;
}
